import React, { useEffect, useRef, useState } from 'react';
import { Typography, Paper, IconButton, Link, Collapse, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { keyframes } from '@mui/system';

import { useKitchen } from '../../contexts/KitchenContext';
import { useAuth } from '../../AuthContext';

import { saveKitchenData } from '../../services/kitchenService';

const streetDrift = keyframes`
  0% {
    background-position: 0 0, 0 0, 0 0, 0 0;
  }
  50% {
    background-position: 32px 18px, -24px 14px, 18px -26px, 0 0;
  }
  100% {
    background-position: 0 0, 0 0, 0 0, 0 0;
  }
`;

const markerPulse = keyframes`
  0% {
    transform: translate(-50%, -50%) rotate(-45deg) scale(1);
    box-shadow: 0 6px 14px rgba(0,0,0,0.22);
  }
  50% {
    transform: translate(-50%, -50%) rotate(-45deg) scale(1.06);
    box-shadow: 0 8px 18px rgba(0,0,0,0.26);
  }
  100% {
    transform: translate(-50%, -50%) rotate(-45deg) scale(1);
    box-shadow: 0 6px 14px rgba(0,0,0,0.22);
  }
`;

export default function KitchenReadiness({ user }) {
  const { auth } = useAuth();
  const { currentKitchenData, setCurrentKitchenData } = useKitchen();
  const [expandedChecks, setExpandedChecks] = useState({});
  const [showPublicDialog, setShowPublicDialog] = useState(false);
  const [hasDeferredPublicLaunch, setHasDeferredPublicLaunch] = useState(false);
  const wasAllCompleteRef = useRef(false);
  const kitchenExists = Boolean(currentKitchenData?.id > 0);

  const toggleCheckDetails = (checkId) => {
    setExpandedChecks((prev) => ({
      ...prev,
      [checkId]: !prev[checkId]
    }));
  };
  
  // Define readiness checks
  const checks = [
    {
      id: 'location-info',
      label: 'Complete Kitchen Basic Information',
      href: '/dashboard#kitchen-settings-section',
      tooltip: 'Complete basic info about your kitchen and a valid address that can be located in maps.',
      completed: currentKitchenData?.address && 
                 currentKitchenData?.city && 
                 currentKitchenData?.state &&
                 currentKitchenData?.phoneNumber &&
                 currentKitchenData?.latitude &&
                 currentKitchenData?.longitude
    },
    {
      id: 'kitchen-menus',
      label: 'Complete Kitchen Menus',
      href: '/dashboard#kitchen-menus-section',
      tooltip: 'You need at least one menu with items in it. Make sure to activate the menu and its items to have them available for customers to order.',
      completed: currentKitchenData?.menus && 
                 currentKitchenData.menus.length > 0 && 
                 currentKitchenData.menus.some(menu => 
                   menu.isActive &&
                   menu.items && 
                   menu.items.length > 0 &&
                   menu.items.some(menuitem => menuitem.isActive)
                 )
    },
    {
      id: 'delivery-options',
      label: 'Complete Delivery Options',
      href: '/dashboard#kitchen-services-section',
      tooltip: 'At least one delivery option checked (Delivery/Pickup)',
      completed: currentKitchenData?.providesDelivery || currentKitchenData?.acceptsPickup
    },
    {
      id: 'chef-info',
      label: 'Complete Chef Information',
      href: '/profile',
      tooltip: 'Complete First and Last Name, Email, Profile Picture, and Self Introduction',
      completed: user?.firstName && 
                 user?.lastName && 
                 user?.email &&
                 user?.profilePic &&
                 user?.selfIntroduction
    },
    {
      id: 'chef-state-regulation-acknowledgment',
      label: 'Complete Acknowledgment',
      href: '/profile#compliance-acknowledgment-section',
      tooltip: 'You need to acknowledge compliance with state food preparation and sales regulations',
      completed: user?.chef?.complianceAcknowledgment
    }
  ];

  const completedCount = checks.filter(check => check.completed).length;
  const totalChecks = checks.length;
  const allComplete = completedCount === totalChecks;
  const isKitchenPublic = allComplete && currentKitchenData?.isReady === true && !hasDeferredPublicLaunch;
  const isReadyToGoPublic = allComplete && !isKitchenPublic;
  const [isPanelExpanded, setIsPanelExpanded] = useState(!allComplete);

  const persistKitchenReadiness = (isReadyValue, options = {}) => {
    const { closeDialog = true, deferPrompt = false } = options;

    if (!kitchenExists) {
      return;
    }

    if (isReadyValue) {
      setHasDeferredPublicLaunch(false);
    }

    if (deferPrompt) {
      setHasDeferredPublicLaunch(true);
    }

    saveKitchenData({ ...currentKitchenData, isReady: isReadyValue }, auth).then((updatedKitchenFromDB) => {
      if (updatedKitchenFromDB) {
        setCurrentKitchenData(updatedKitchenFromDB.kitchen);
      }
    }).catch((error) => {
      console.error('Error saving kitchen readiness status:', error);
    }).finally(() => {
      if (closeDialog) {
        setShowPublicDialog(false);
      }
    });
  };

  const handleConfirmPublic = () => {
    persistKitchenReadiness(true, { closeDialog: true });
  };

  const handleNotReadyYet = () => {
    persistKitchenReadiness(false, { closeDialog: true, deferPrompt: true });
  };

  useEffect(() => {
    const becameAllComplete = !wasAllCompleteRef.current && allComplete;

    if (!kitchenExists) {
      wasAllCompleteRef.current = allComplete;
      return;
    }

    if (!allComplete) {
      setShowPublicDialog(false);
      setHasDeferredPublicLaunch(false);
      wasAllCompleteRef.current = false;

      if (currentKitchenData.isReady !== false) {
        saveKitchenData({ ...currentKitchenData, isReady: false }, auth).then((updatedKitchenFromDB) => {
          if (updatedKitchenFromDB) {
            setCurrentKitchenData(updatedKitchenFromDB.kitchen);
          }
        }).catch((error) => {
          console.error('Error saving kitchen readiness status:', error);
        });
      }

      return;
    }

    if (currentKitchenData.isReady === true) {
      setHasDeferredPublicLaunch(false);
      setShowPublicDialog(false);
      wasAllCompleteRef.current = true;
      return;
    }

    if (hasDeferredPublicLaunch || !becameAllComplete) {
      setShowPublicDialog(false);
      wasAllCompleteRef.current = true;
      return;
    }

    setShowPublicDialog(true);
    wasAllCompleteRef.current = true;

  }, [allComplete, auth, currentKitchenData, hasDeferredPublicLaunch, kitchenExists]);

  useEffect(() => {
    setIsPanelExpanded(!allComplete);
  }, [allComplete]);

  if (!kitchenExists) {
    return null;
  }

  return (
    <Paper 
      elevation={2} 
      style={{ 
        padding: 16, 
        marginTop: 16, 
        marginBottom: 16,
        backgroundColor: allComplete ? '#e8f5e9' : '#fff3e0'
      }}
      data-testid="kitchen-readiness-panel"
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography variant="h6">
            {isKitchenPublic ? 'Your Kitchen is Public!' : isReadyToGoPublic ? 'Kitchen Ready to Go!' : 'Steps to Go Public!'}
          </Typography>

          {isPanelExpanded && (
            <Typography variant="caption" color="text.secondary" style={{ display: 'block' }}>
              {completedCount} of {totalChecks} completed
            </Typography>
          )}
          

          {!isPanelExpanded && allComplete && (
            <Typography
              variant="body2"
              style={{ color: isKitchenPublic ? '#2e7d32' : '#ef6c00', fontWeight: 600 }}
              data-testid="kitchen-readiness-collapsed-message"
            >
              {isKitchenPublic ? 'You are all set' : 'One step away from going public'}
            </Typography>
          )}
        </div>

        <IconButton
          size="small"
          onClick={() => setIsPanelExpanded((prev) => !prev)}
          aria-label={isPanelExpanded ? 'Collapse kitchen readiness panel' : 'Expand kitchen readiness panel'}
          aria-expanded={isPanelExpanded}
          data-testid="kitchen-readiness-toggle"
        >
          <ExpandMoreIcon
            style={{
              transform: isPanelExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 120ms ease-in-out'
            }}
          />
        </IconButton>
      </div>

      <Collapse in={isPanelExpanded} timeout="auto" unmountOnExit>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }} data-testid="kitchen-readiness-content">
        {checks.map((check) => (
          <div 
            key={check.id} 
            style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }}
            data-testid={`check-${check.id}`}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {check.completed ? (
                <CheckCircleIcon 
                  style={{ color: '#4caf50', fontSize: 20 }} 
                  data-testid={`icon-${check.id}-completed`}
                />
              ) : (
                <RadioButtonUncheckedIcon 
                  style={{ color: '#9e9e9e', fontSize: 20 }} 
                  data-testid={`icon-${check.id}-incomplete`}
                />
              )}
              {check.completed ? (
                <Typography
                  component="span"
                  data-testid={`label-${check.id}`}
                  sx={{
                    display: 'block',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    color: 'success.dark',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    lineHeight: 1.4,
                    flex: 1
                  }}
                >
                  {check.label}
                </Typography>
              ) : (
                <Link
                  href={check.href}
                  underline="none"
                  data-testid={`link-${check.id}`}
                  sx={{
                    display: 'block',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    lineHeight: 1.4,
                    flex: 1,
                    transition: 'background-color 120ms ease-in-out',
                    '&:hover': {
                      textDecoration: 'none',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  {check.label}
                </Link>
              )}
              <IconButton 
                size="small" 
                style={{ padding: 4 }}
                onClick={() => toggleCheckDetails(check.id)}
                aria-label={`Toggle details for ${check.label}`}
                aria-expanded={Boolean(expandedChecks[check.id])}
                data-testid={`help-${check.id}`}
              >
                <ExpandMoreIcon 
                  style={{ 
                    fontSize: 22,
                    color: '#757575',
                    transform: expandedChecks[check.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 120ms ease-in-out'
                  }} 
                />
              </IconButton>
            </div>
            <Collapse in={Boolean(expandedChecks[check.id])} timeout="auto" unmountOnExit>
              <Typography
                variant="caption"
                color="text.secondary"
                style={{ display: 'block', marginLeft: 28, marginRight: 4, marginTop: 2 }}
                data-testid={`details-${check.id}`}
              >
                {check.tooltip}
              </Typography>
            </Collapse>
          </div>
        ))}
        </div>

        {isKitchenPublic && (
          <Typography 
            variant="caption" 
            style={{ 
              display: 'block', 
              marginTop: 12, 
              color: '#2e7d32', 
              fontWeight: 500 
            }}
          >
            ✓ Your kitchen is now ready!
          </Typography>
        )}

        {isReadyToGoPublic && (
          <Typography
            variant="caption"
            style={{
              display: 'block',
              marginTop: 12,
              color: '#ef6c00',
              fontWeight: 500
            }}
          >
            ✓ All setup steps are complete. Tap{' '}
            <Button
              onClick={handleConfirmPublic}
              size="small"
              variant="text"
              color="primary"
              style={{
                textTransform: 'none',
                minWidth: 'auto',
                padding: 0,
                fontWeight: 700,
                fontSize: '0.80rem',
                verticalAlign: 'baseline'
              }}
            >
              READY Now
            </Button>{' '}
            when you want to go public.
          </Typography>
        )}
      </Collapse>

      <Dialog
        open={showPublicDialog}
        onClose={() => {}}
        disableEscapeKeyDown
      >
        <DialogTitle>Officially Going Public</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            At this point, your kitchen is READY to be public and visible to customers on the internet.
          </Typography>

          <Box
            aria-hidden="true"
            sx={{
              width: '100%',
              maxWidth: 320,
              height: 180,
              mx: 'auto',
              my: 2,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #cfd8dc',
              position: 'relative',
              background: 'linear-gradient(180deg, #f4f8fb 0%, #e8eff6 100%)',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.55)'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                  linear-gradient(rgba(255,255,255,0.35) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(255,255,255,0.35) 1px, transparent 1px),
                  linear-gradient(135deg, rgba(66,133,244,0.20) 0 12%, transparent 12% 24%, rgba(52,168,83,0.22) 24% 26%, transparent 26% 100%),
                  linear-gradient(45deg, transparent 0 44%, rgba(66,133,244,0.18) 44% 46%, transparent 46% 100%)
                `,
                backgroundSize: '28px 28px, 28px 28px, 140px 140px, 120px 120px',
                backgroundPosition: '0 0, 0 0, 0 0, 0 0',
                opacity: 0.98,
                animation: `${streetDrift} 8s ease-in-out infinite`
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                  radial-gradient(circle at 18% 28%, rgba(255,255,255,0.35) 0 2px, transparent 3px),
                  radial-gradient(circle at 70% 22%, rgba(255,255,255,0.30) 0 2px, transparent 3px),
                  radial-gradient(circle at 26% 74%, rgba(255,255,255,0.28) 0 2px, transparent 3px),
                  radial-gradient(circle at 78% 66%, rgba(255,255,255,0.26) 0 2px, transparent 3px)
                `,
                backgroundRepeat: 'no-repeat',
                filter: 'blur(0.1px)',
                opacity: 0.9,
                animation: `${streetDrift} 10s ease-in-out infinite reverse`
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 64,
                height: 64,
                backgroundColor: '#e53935',
                borderRadius: '50% 50% 50% 0',
                transform: 'translate(-50%, -50%) rotate(-45deg)',
                boxShadow: '0 6px 14px rgba(0,0,0,0.22)',
                animation: `${markerPulse} 1.8s ease-in-out infinite`
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                  boxShadow: '0 0 0 4px rgba(255,255,255,0.55)'
                }}
              />
            </Box>
          </Box>

        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleNotReadyYet}
            variant="outlined"
            color="inherit"
            style={{ textTransform: 'none' }}
          >
            I’m not Ready yet
          </Button>
          <Button
            onClick={handleConfirmPublic}
            variant="contained"
            color="primary"
            style={{ textTransform: 'none' }}
          >
            READY Now
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
